import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Chip } from '../components/Chip';
import { APIError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { safeNext } from '../lib/safeNext';

export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'ok' | 'error'>('verifying');
  const [message, setMessage] = useState<string>('Verifying link…');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Missing token in the link.');
      return;
    }
    (async () => {
      try {
        const { user } = await api.verifyMagicLink(token);
        setAuthed(user);
        setStatus('ok');
        navigate(safeNext(params.get('next')), { replace: true });
      } catch (err) {
        let msg = 'Could not verify the link.';
        if (err instanceof APIError) {
          const body = err.body as { error?: string };
          msg = body?.error || msg;
        }
        setStatus('error');
        setMessage(msg);
      }
    })();
  }, [params, navigate, setAuthed]);

  return (
    <div className="min-h-full max-w-sm mx-auto px-6 py-16 space-y-6">
      <header className="space-y-3">
        <p className="label-eyebrow">Gios Protocol</p>
        <h1 className="font-display text-6xl leading-none">Sign in</h1>
      </header>

      {/* Same shape as the sign-in screen: ink headline on the canvas, the
          live status on a white sheet underneath it. */}
      <div className="card p-5 sm:p-6 space-y-4">
        {status === 'verifying' && (
          <Chip tone="muted">{message}</Chip>
        )}
        {status === 'ok' && (
          <Chip tone="good" dot>Signed in. Redirecting…</Chip>
        )}
        {status === 'error' && (
          <div className="space-y-4">
            <Chip tone="danger" dot>{message}</Chip>
            <Link to="/login" className="btn-primary block text-center px-5 py-3 text-sm">
              Request a new link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
